import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    ThemeProvider,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {
    RefObject,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { useAppSelector } from "../../hooks/reduxHooks";
import useNumberFormatter from "../../hooks/useNumberFormatter";
import { Tutorial, useTutorialContext } from "./TutorialContext";
import TutorialBackdrop from "./components/tutorialBackdrop";
import useCalculatePositionStyle from "./hooks/useCalculatePositionStyle";
import { TPosition } from "./models/TPosition";
import { TStyle } from "./models/TStyle";

const fullContentStyle = {
    width: "85%",
    height: "85dvh",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1500,
};

type TProps = {
    steps: Tutorial["step"][];
    children?: React.ReactNode;
    position?: TPosition;
    renderChildren?: (
        wrapperAction: Tutorial["action"],
        control: {
            ref: RefObject<HTMLElement | null>;
        }
    ) => React.ReactNode;
    forDialog?: boolean;
    onNextStep?: (activeStep: string) => void;
    onPreviousStep?: (...args: any[]) => void;
};

export default function TutorialWrapper({
    children,
    steps,
    position = "left",
    renderChildren,
    forDialog = false,
    onNextStep,
    onPreviousStep,
}: TProps) {
    const { tutorials, activeStep, nextStep, previousStep, globalMenu } =
        useTutorialContext();
    const holderRef = useRef<HTMLElement | null>(null);
    const [style, setStyle] = useState<TStyle | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const calculatePositionStyle = useCalculatePositionStyle();
    const formatter = useNumberFormatter();
    const sharedState = useAppSelector((state) => state.shared);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const activeStepData = useMemo(
        () => tutorials.find((s) => s.step === activeStep),
        [tutorials, activeStep]
    );

    const center = useMemo(
        () => !holderRef.current,
        [activeStep, holderRef.current]
    );

    const positionStyle = useMemo(
        () => calculatePositionStyle(center ? null : style, position),
        [position, style, center]
    );

    const isActive = useMemo(
        () =>
            activeStep &&
            steps.includes(activeStep) &&
            !!tutorials.find((s) => steps.some((step) => step === s.step)),
        [activeStep, steps]
    );

    const showActions = useMemo(
        () =>
            !activeStepData?.couldNotStepBack || !activeStepData?.couldNotSkip,
        [activeStepData]
    );

    useLayoutEffect(() => {
        if (!isActive) return;

        if (holderRef.current) {
            setStyle({
                top: holderRef.current?.getBoundingClientRect().top,
                left: holderRef.current?.getBoundingClientRect().left,
                width: holderRef.current?.getBoundingClientRect().width,
                height: holderRef.current?.getBoundingClientRect().height,
            });
        }
    }, [activeStep, holderRef, isActive]);

    const handlePreviousStep = () => {
        if (!isActive) return;

        onPreviousStep?.();
        previousStep();
    };

    const handleNextStep = () => {
        if (!isActive) return;

        onNextStep?.(activeStep);
        nextStep();
    };

    const wrapperAction = (action?: Tutorial["action"]) => {
        if (!isActive) return;

        if (action) {
            action();
        } else {
            const stepDetail = tutorials.find((s) => s.step === activeStep);
            stepDetail?.action?.();
        }
        handleNextStep();
    };

    const control = {
        ref: holderRef,
    };

    const GlobalMenu = useMemo(() => globalMenu, [globalMenu]);

    const isFinalStep =
        tutorials.indexOf(activeStepData) + 1 == tutorials.length;

    return (
        <>
            {isActive && (
                <>
                    {renderChildren
                        ? renderChildren(wrapperAction, control)
                        : children}

                    {isActive && (
                        <TutorialBackdrop
                            style={style}
                            isActive={isActive}
                            forDialog={forDialog}
                        />
                    )}

                    {createPortal(
                        <Card
                            id="tutorial-card"
                            ref={cardRef}
                            role="dialog"
                            style={{
                                backgroundColor: "white",
                                position: "fixed",
                                marginTop: "0",
                                maxHeight: "85dvh",
                                overflow: "auto",
                                ...(activeStepData?.fullContent
                                    ? fullContentStyle
                                    : positionStyle),
                                width: isSmallScreen ? "100%" : 500,
                                zIndex: 1500,
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                            }}
                        >
                            <CardHeader title={activeStepData?.title} />
                            <CardContent
                                sx={{
                                    height: activeStepData?.fullContent
                                        ? "calc(85dvh - 100px)"
                                        : "auto",
                                }}
                            >
                                {activeStepData?.hint}
                            </CardContent>
                            {showActions && (
                                <CardActions>
                                    {!activeStepData?.couldNotStepBack && (
                                        <Button
                                            color="warning"
                                            variant="outlined"
                                            onClick={handlePreviousStep}
                                        >
                                            مرحله قبل
                                        </Button>
                                    )}
                                    <span style={{ flex: 1 }}></span>
                                    {!activeStepData?.couldNotSkip && (
                                        <ThemeProvider
                                            theme={{
                                                ...theme,
                                                palette: {
                                                    ...theme.palette,
                                                    primary: {
                                                        main: "#0b9b9d",
                                                    },
                                                },
                                            }}
                                        >
                                            {!isFinalStep && (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNextStep}
                                                    sx={{ color: "white" }}
                                                >
                                                    ادامه (
                                                    {formatter.format(
                                                        tutorials.indexOf(
                                                            activeStepData
                                                        ) + 1
                                                    )}
                                                    /
                                                    {formatter.format(
                                                        tutorials.length
                                                    )}
                                                    )
                                                </Button>
                                            )}
                                            {isFinalStep && (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNextStep}
                                                    sx={{ color: "white" }}
                                                >
                                                    {sharedState.accountDetail
                                                        .shouldRunOnboarding
                                                        ? "دریافت جایزه"
                                                        : "پایان"}
                                                </Button>
                                            )}
                                        </ThemeProvider>
                                    )}
                                </CardActions>
                            )}
                        </Card>,
                        document.body
                    )}
                </>
            )}

            {!isActive && (
                <>
                    {renderChildren
                        ? renderChildren(wrapperAction, control)
                        : children}
                </>
            )}

            {isActive && GlobalMenu}
        </>
    );
}
